import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Mail, Link2, Trash2, ExternalLink, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { money } from "@/components/equity/AssumptionsPanel";

export function GrantLinkDialog({ grant, open, onOpenChange, onChanged }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [link, setLink] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!grant) return;
    setForm({ holder_email: grant.holder_email || "", option_type: grant.option_type || "ISO", vesting_start: grant.vesting_start || "",
      vesting_months: grant.vesting_months ?? 48, cliff_months: grant.cliff_months ?? 12 });
    setLink(grant.grant_token ? `${window.location.origin}/equity/${grant.grant_token}` : null);
    api.get(`/securities/${grant.id}/valuation`).then(({ data }) => setPreview(data)).catch(() => setPreview(null));
  }, [grant]);

  if (!grant) return null;
  const saveDetails = async () => {
    setSaving(true);
    try {
      await api.put(`/securities/${grant.id}/grant-details`, {
        holder_email: form.holder_email || null, option_type: form.option_type, vesting_start: form.vesting_start || null,
        vesting_months: Number(form.vesting_months), cliff_months: Number(form.cliff_months),
      });
      toast.success("Grant details saved");
      onChanged?.();
      const { data } = await api.get(`/securities/${grant.id}/valuation`); setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const createLink = async (sendEmail) => {
    setLinking(true);
    try {
      if (sendEmail && form.holder_email && form.holder_email !== grant.holder_email) await saveDetails();
      const { data } = await api.post(`/securities/${grant.id}/grant-link`, { send_email: sendEmail });
      setLink(data.url);
      toast.success(sendEmail ? (data.emailed ? `Emailed to ${form.holder_email}` : "Link created (email not sent)") : "Private link ready");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create link"); }
    finally { setLinking(false); }
  };
  const revoke = async () => {
    try { await api.delete(`/securities/${grant.id}/grant-link`); setLink(null); toast.success("Link revoked"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Copied"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="grant-link-dialog">
        <DialogHeader>
          <DialogTitle>Share equity page with {grant.holder_name}</DialogTitle>
          <DialogDescription>{Math.round(grant.shares).toLocaleString()} options{grant.strike_price ? ` at ${money(grant.strike_price, 2)}` : ""}. The employee sees vesting, today's value, exit scenarios and tax estimates - never the rest of the cap table.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Employee email</Label><Input type="email" value={form.holder_email} onChange={(e) => setForm((f) => ({ ...f, holder_email: e.target.value }))} placeholder="name@company.com" className="mt-1 h-9" data-testid="grant-email" /></div>
          <div>
            <Label className="text-xs">Option type</Label>
            <Select value={form.option_type} onValueChange={(v) => setForm((f) => ({ ...f, option_type: v }))}>
              <SelectTrigger className="mt-1 h-9" data-testid="grant-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="ISO">ISO</SelectItem><SelectItem value="NSO">NSO</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Vesting start</Label><DatePicker value={form.vesting_start} onChange={(v) => setForm((f) => ({ ...f, vesting_start: v }))} className="mt-1 h-9" data-testid="grant-vesting-start" /></div>
          <div><Label className="text-xs">Vesting months</Label><Input type="number" min={0} value={form.vesting_months} onChange={(e) => setForm((f) => ({ ...f, vesting_months: e.target.value }))} className="mt-1 h-9" data-testid="grant-vesting-months" /></div>
          <div><Label className="text-xs">Cliff months</Label><Input type="number" min={0} value={form.cliff_months} onChange={(e) => setForm((f) => ({ ...f, cliff_months: e.target.value }))} className="mt-1 h-9" data-testid="grant-cliff" /></div>
          <div className="col-span-2 flex justify-end"><Button type="button" size="sm" variant="outline" disabled={saving} onClick={saveDetails} className="gap-1.5" data-testid="grant-save-details">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save details</Button></div>
        </div>
        {preview && (
          <div className="grid grid-cols-3 gap-2 rounded-lg bg-secondary/60 p-3 text-xs" data-testid="grant-preview">
            <div><div className="text-muted-foreground">Vested today</div><div className="font-semibold text-foreground">{money(preview.today.intrinsic_vested)}</div></div>
            <div><div className="text-muted-foreground">Fair value (BS)</div><div className="font-semibold text-foreground">{money(preview.today.black_scholes_total_fmv ?? preview.today.black_scholes_total_preferred)}</div></div>
            <div><div className="text-muted-foreground">Break-even exit</div><div className="font-semibold text-foreground">{preview.break_even ? money(preview.break_even.exit_value) : "-"}</div></div>
            {!preview.company.fmv && <div className="col-span-3 text-amber-700">Set the 409A FMV in Employee equity settings so today's value is accurate.</div>}
          </div>
        )}
        {link ? (
          <div className="space-y-2" data-testid="grant-link-ready">
            <div className="flex items-center gap-2 rounded-lg border bg-card p-2">
              <Link2 className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate text-xs" data-testid="grant-link-url">{link}</span>
              <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={copy} data-testid="grant-link-copy"><Copy className="h-3.5 w-3.5" /> Copy</Button>
              <Button asChild size="sm" variant="ghost" className="h-7 text-xs"><a href={link} target="_blank" rel="noreferrer" data-testid="grant-link-open"><ExternalLink className="h-3.5 w-3.5" /></a></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" className="gap-1.5" disabled={linking || !form.holder_email} onClick={() => createLink(true)} data-testid="grant-link-email">{linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Email link to employee</Button>
              <Button type="button" size="sm" variant="ghost" className="gap-1.5 text-red-600" onClick={revoke} data-testid="grant-link-revoke"><Trash2 className="h-3.5 w-3.5" /> Revoke link</Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" className="gap-1.5" disabled={linking} onClick={() => createLink(false)} data-testid="grant-link-create">{linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Create private link</Button>
            <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={linking || !form.holder_email} onClick={() => createLink(true)} data-testid="grant-link-create-email"><Mail className="h-3.5 w-3.5" /> Create &amp; email</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
